/**
 * RBAC-3.7 · the By capability tab: "who can do this", asked from the capability rather than the
 * role (FR-17a, UX-DR12), and comparison as a selection of two or three roles (FR-17b, UX-DR13).
 *
 * It is RBAC-3.5's editor read sideways, and restates none of it. A capability is one rung of a
 * ladder ("Contracts: View") or one other editor row; holding it, its words and what a toggle writes
 * all come from `permissionEditor.ts`. So a toggle here and a control in the role editor produce
 * byte-identical drafts, and RBAC-3.6's diff and RBAC-1.3's reserved refusal see one kind of edit.
 *
 * ⚠️ FR-5: a Joules role shows its state and NO toggle. The design drew toggles on Joules roles; the
 * spec forbids it (decision log, 2026-09-10, "a genuine conflict between the design and the spec").
 *
 * ⚠️ People are counted per role and never summed. `holderCount` is distinct per role, but someone
 * holding two roles would be counted twice in a total: the mockup's "207 people" was that error.
 *
 * Pure, no DOM, no Supabase.
 */
import type { AccountRole } from './accountRoles';
import type { PermissionArea } from './permissionDisplay';
import { applyLadder, applySwitch, buildEditor, changedItemCount, rungSummary, type EditorItem, type LadderItem } from './permissionEditor';
import { NOUNS } from './permissionControls';

export const OVERRIDE = 'system.override';

export interface Capability {
  /** `rung:<noun>:<rung>` or `key:<first key>`. Stable, used in the URL. */
  id: string;
  area: PermissionArea;
  kind: 'rung' | 'switch' | 'destructive' | 'locked';
  label: string;
  description: string;
  /** Every string this capability's control writes. */
  keys: string[];
  noun?: string;
  rung?: string;
  /** Destructive only: what using it cannot take back (UX-DR7). */
  irreversible?: string;
}

const nounKeys = (noun: string): string[] => {
  const n = NOUNS[noun];
  return [...new Set([...n.capability.flatMap((c) => c.strings), ...(n.scope?.team ?? []), ...(n.scope?.all ?? [])])];
};

function capabilitiesOf(area: PermissionArea, item: EditorItem): Capability[] {
  if (item.kind === 'ladder') {
    return item.rungs
      .filter((r): r is { value: string; label: string } => r.value !== null)
      .map((r) => ({
        id: `rung:${item.noun}:${r.value}`,
        area,
        kind: 'rung',
        label: `${item.title}: ${r.label}`,
        description: rungSummary(item.noun, r.value),
        keys: nounKeys(item.noun),
        noun: item.noun,
        rung: r.value,
      }));
  }
  return [{
    id: `key:${item.keys[0]}`,
    area,
    kind: item.kind,
    label: item.label,
    description: item.description,
    keys: [...item.keys],
    ...(item.kind === 'destructive' ? { irreversible: item.irreversible } : {}),
  }];
}

const CAPABILITIES: Capability[] = buildEditor([]).flatMap((a) => a.items.flatMap((i) => capabilitiesOf(a.area, i)));

export function listCapabilities(): Capability[] {
  return CAPABILITIES;
}

export function findCapability(id: string | null | undefined): Capability | null {
  return CAPABILITIES.find((c) => c.id === id) ?? null;
}

export function filterCapabilities(caps: readonly Capability[], query: string): Capability[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...caps];
  return caps.filter((c) => `${c.label} ${c.area} ${c.description}`.toLowerCase().includes(q));
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading a role
// ─────────────────────────────────────────────────────────────────────────────

/** Draft permissions by role id. A role with no entry reads as saved. */
export type Drafts = Readonly<Record<number, readonly string[]>>;

export const effectivePermissions = (role: AccountRole, drafts: Drafts): readonly string[] =>
  drafts[role.id] ?? role.permissions;

const itemsOf = (perms: readonly string[]) => buildEditor(perms).flatMap((a) => a.items);

const sameItem = (cap: Capability) => (i: EditorItem) =>
  cap.kind === 'rung' ? i.kind === 'ladder' && i.noun === cap.noun : i.kind !== 'ladder' && i.keys[0] === cap.keys[0];

const rungIndex = (item: LadderItem, rung: string | null) => item.rungs.findIndex((r) => r.value === rung);

function readCapability(cap: Capability, perms: readonly string[]): { holds: boolean; state: string; item: EditorItem } {
  const item = itemsOf(perms).find(sameItem(cap));
  if (!item) throw new Error(`capabilityLookup: ${cap.id} has no editor row`);
  switch (item.kind) {
    case 'ladder':
      return { holds: rungIndex(item, item.capability) >= rungIndex(item, cap.rung!), state: item.state, item };
    case 'locked':
      return { holds: item.held, state: item.held ? 'Has it' : 'Does not have it', item };
    default:
      return { holds: item.on, state: item.on ? 'On' : 'Off', item };
  }
}

export const OVERRIDE_STATE = 'Everything, by override';
export const JOULES_READ_ONLY = 'Joules roles are read only. Duplicate or adopt it to change this.';
export const RESERVED_REASON = 'Only Joules can grant it.';
export const OVERRIDE_REASON = 'This role overrides every permission, so there is nothing to turn on or off.';

export interface RoleHolding {
  role: AccountRole;
  holds: boolean;
  /** "Edit, their team", "On", or the override sentence. */
  state: string;
  /** The toggle's position, or null when there is no toggle (and `reason` says why). */
  toggle: 'on' | 'off' | null;
  reason: string | null;
  /** The draft differs from the saved role on this capability's control. */
  changed: boolean;
}

export interface WhoHolds {
  holding: RoleHolding[];
  without: RoleHolding[];
  /** "3 of 21 roles have it · 18 do not". Never a people total. */
  summary: string;
}

const byKindPeopleName = (a: RoleHolding, b: RoleHolding) =>
  Number(a.role.isGlobal) - Number(b.role.isGlobal) ||
  b.role.holderCount - a.role.holderCount ||
  a.role.name.localeCompare(b.role.name);

function holding(cap: Capability, role: AccountRole, drafts: Drafts): RoleHolding {
  const perms = effectivePermissions(role, drafts);
  if (perms.includes(OVERRIDE)) {
    return { role, holds: true, state: OVERRIDE_STATE, toggle: null, reason: OVERRIDE_REASON, changed: false };
  }
  const now = readCapability(cap, perms);
  const saved = readCapability(cap, role.permissions);
  const reason = role.isGlobal ? JOULES_READ_ONLY : cap.kind === 'locked' ? RESERVED_REASON : null;
  return {
    role,
    holds: now.holds,
    state: now.state,
    toggle: reason ? null : now.holds ? 'on' : 'off',
    reason,
    changed: now.state !== saved.state || now.holds !== saved.holds,
  };
}

/** AC 1: every role, split by whether it holds `cap`, account roles first in each group. */
export function whoHolds(cap: Capability, roles: readonly AccountRole[], drafts: Drafts): WhoHolds {
  const all = roles.map((r) => holding(cap, r, drafts));
  const has = all.filter((h) => h.holds).sort(byKindPeopleName);
  const not = all.filter((h) => !h.holds).sort(byKindPeopleName);
  return {
    holding: has,
    without: not,
    summary: `${has.length} of ${all.length} ${all.length === 1 ? 'role has' : 'roles have'} it · ${not.length} ${not.length === 1 ? 'does' : 'do'} not`,
  };
}

/**
 * AC 2: a role's strings after turning `cap` on or off, through the editor's own `applyLadder` and
 * `applySwitch`. Off on a rung drops to the rung below it, so "cannot manage contracts" still sees them.
 */
export function toggleCapability(perms: readonly string[], cap: Capability, on: boolean): string[] {
  if (cap.kind === 'locked') throw new Error(`capabilityLookup: ${cap.id} is reserved; ${RESERVED_REASON}`);
  if (cap.kind !== 'rung') return applySwitch(perms, cap.keys, on);
  const { item, holds } = readCapability(cap, perms);
  const ladder = item as LadderItem;
  if (on === holds) return [...perms].sort();
  const scope = ladder.scope?.value ?? null;
  const target = on ? cap.rung! : ladder.rungs[rungIndex(ladder, cap.rung!) - 1].value;
  return applyLadder(perms, cap.noun!, target, scope);
}

/** The roles whose draft would change something on screen. Per control, as the editor counts. */
export function draftedRoles(roles: readonly AccountRole[], drafts: Drafts): AccountRole[] {
  return roles.filter((r) => drafts[r.id] && changedItemCount(r.permissions, drafts[r.id]) > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparison (FR-17b, UX-DR13)
// ─────────────────────────────────────────────────────────────────────────────

export const COMPARE_MIN = 2;
export const COMPARE_MAX = 3;

/** UX-DR13: the affordance states why, rather than silently truncating. */
export const COMPARE_REASON =
  'Pick two or three roles to see every capability lined up. Not all of them at once: a grid that wide stops being readable, and its header scrolls away from the row you are reading.';

/** Add or remove a role from the selection. A fourth is refused, never swapped in for the first. */
export function toggleCompare(selected: readonly number[], id: number): number[] {
  if (selected.includes(id)) return selected.filter((s) => s !== id);
  if (selected.length >= COMPARE_MAX) return [...selected];
  return [...selected, id];
}

export interface CompareRow {
  label: string;
  cells: string[];
  differs: boolean;
}

export interface CompareArea {
  area: PermissionArea;
  rows: CompareRow[];
}

const cellOf = (i: EditorItem): string => {
  switch (i.kind) {
    case 'ladder':
      return i.state;
    case 'locked':
      return i.held ? 'Yes' : 'No';
    default:
      return i.on ? 'Yes' : 'No';
  }
};

const rowLabel = (i: EditorItem) => (i.kind === 'ladder' ? i.title : i.label);

/** Every editor control, one column per role, in the editor's own order. Drafts are read. */
export function compareRows(roles: readonly AccountRole[], drafts: Drafts): CompareArea[] {
  if (roles.length < COMPARE_MIN || roles.length > COMPARE_MAX) {
    throw new Error(`capabilityLookup: comparison takes ${COMPARE_MIN} to ${COMPARE_MAX} roles, not ${roles.length}`);
  }
  const editors = roles.map((r) => {
    const perms = effectivePermissions(r, drafts);
    return { override: perms.includes(OVERRIDE), areas: buildEditor(perms) };
  });
  return editors[0].areas.map((a, ai) => ({
    area: a.area,
    rows: a.items.map((item, ii) => {
      const cells = editors.map((e) => (e.override ? OVERRIDE_STATE : cellOf(e.areas[ai].items[ii])));
      return { label: rowLabel(item), cells, differs: new Set(cells).size > 1 };
    }),
  }));
}
